import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { usePreviousDistinct } from 'react-use';
import {
  CompanyQrDocument,
  CompanyQrQuery,
  CompanyQrStatusChangedDocument,
  DeviceState,
} from '../../../graphql/graphql-operations';
import useQueryErrorSnackbar from '../../../hooks/useQueryErrorSnackbar';

const CHARGED_BATTERY_TRESHOLD = 40;
type VrDevice = CompanyQrQuery['companyQr']['vrDevice'];
export type QrDeviceState = {
  primary: boolean;
  device?: VrDevice;
};

export type QrVideoIds = {
  primaryHeadsetWakeupVideoId: string;
  backupHeadsetWakeupVideoId: string;
  launchInstructionVideoId: string;
  primaryHeadsetInstructionVideoId: string;
  backupHeadsetInstructionVideoId: string;
  returnHeadsetInstructionVideoId: string;
};
export const useQrData = (
  id: string,
  language: string,
  vrDeviceId: string | undefined,
) => {
  const {
    data,
    refetch: qrRefetch,
    error: qrError,
    subscribeToMore,
  } = useQuery(CompanyQrDocument, {
    variables: {
      input: {
        id: id,
        language: language,
      },
    },
  });
  useQueryErrorSnackbar(qrError, qrRefetch);

  useEffect(() => {
    return subscribeToMore({
      document: CompanyQrStatusChangedDocument,
      variables: { input: { id: id } },
      updateQuery: (prev, updated) => {
        const { deviceId, charging, screenOn, state, battery } =
          updated.subscriptionData.data.companyQrStatusChanged;

        if (deviceId === prev.companyQr.vrDevice.id) {
          prev = {
            ...prev,
            companyQr: {
              ...prev.companyQr,
              vrDevice: {
                ...prev.companyQr.vrDevice,
                status: {
                  ...prev.companyQr.vrDevice.status,
                  battery: battery,
                  charging: charging,
                  screenOn: screenOn,
                  state: state,
                },
              },
            },
          };
        }

        if (deviceId === prev.companyQr.backupVrDevice?.id) {
          prev = {
            ...prev,
            companyQr: {
              ...prev.companyQr,
              backupVrDevice: {
                ...prev.companyQr.backupVrDevice,
                status: {
                  ...prev.companyQr.backupVrDevice.status,
                  battery: battery,
                  charging: charging,
                  screenOn: screenOn,
                  state: state,
                },
              },
            },
          };
        }

        return prev;
      },
    });
  }, [id, subscribeToMore]);

  const qrDataPrev = usePreviousDistinct(data, (p, next) => !next?.companyQr);
  const qrData = data?.companyQr || qrDataPrev?.companyQr;

  const videoIds: QrVideoIds = useMemo(
    () => ({
      primaryHeadsetWakeupVideoId: qrData?.qrVideos?.primaryHeadsetWakeup || '',
      backupHeadsetWakeupVideoId: qrData?.qrVideos?.backupHeadsetWakeup || '',
      launchInstructionVideoId: qrData?.qrVideos?.launchInstruction || '',
      primaryHeadsetInstructionVideoId:
        qrData?.qrVideos?.primaryHeadsetInstruction || '',
      backupHeadsetInstructionVideoId:
        qrData?.qrVideos?.backupHeadsetInstruction || '',
      returnHeadsetInstructionVideoId:
        qrData?.qrVideos?.returnHeadsetInstruction || '',
    }),
    [qrData?.qrVideos],
  );

  const deviceState: QrDeviceState = useMemo(() => {
    const devices: VrDevice[] = [qrData?.vrDevice, qrData?.backupVrDevice]
      .filter((d) => !!d?.status)
      .sort(
        (d1, d2) => (d2?.status.battery || 0) - (d1?.status.battery || 0),
      ) as VrDevice[]; //Some weird type issue, even tho i filter it it still believes there can be undefined in the array

    const offlineDevices = devices.filter(
      (device) => device.status.state === DeviceState.OFFLINE,
    );
    const chargedDevices = devices.filter(
      (device) => (device.status.battery || 0) > CHARGED_BATTERY_TRESHOLD,
    );

    let selectedDevice = devices[0];
    const preselectedDevice = devices.find(
      (d) => vrDeviceId && d.id === vrDeviceId,
    );
    if (preselectedDevice) {
      selectedDevice = preselectedDevice;
    } else if (chargedDevices.length > 0) {
      //select the most charged device
      selectedDevice = chargedDevices[0];
    } else if (
      (selectedDevice?.status.battery || 0) < CHARGED_BATTERY_TRESHOLD &&
      offlineDevices.length > 0
    ) {
      //Not charged enough so try offline device
      selectedDevice = offlineDevices[0];
    }

    return {
      primary: selectedDevice?.id === qrData?.vrDevice.id,
      device: selectedDevice,
    };
  }, [qrData?.vrDevice.status, qrData?.backupVrDevice?.status, vrDeviceId]);

  return {
    qrData,
    videoIds,
    deviceState,
  };
};
